import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { getCampaignDiscovery, getDashboardData, getCampaignDashboard, syncServerTime } from '../services/api';
import TreasureHuntReport from '../components/TreasureHuntReport';
import GourdBreakingReport from '../components/GourdBreakingReport';
import RankedGourdBreakingReport from '../components/RankedGourdBreakingReport';
import BasketballReport from '../components/BasketballReport';
import ARPosterReport from '../components/ARPosterReport';
import { PeopleFill } from 'react-bootstrap-icons';
import SockJS from 'sockjs-client';
import { Client } from '@stomp/stompjs';
import { formatDate } from '../utils/date';

const ReportPage = () => {
  // State for campaign discovery and selection
  const [campaignKeys, setCampaignKeys] = useState([]);
  const [selectedCampaignKey, setSelectedCampaignKey] = useState(null);
  const [campaignNameMap, setCampaignNameMap] = useState({});
  const [isDiscoveryLoading, setIsDiscoveryLoading] = useState(true);

  // State for dashboard data
  const [dashboardData, setDashboardData] = useState(null);
  const [isDashboardLoading, setIsDashboardLoading] = useState(false);
  const [dashboardError, setDashboardError] = useState(null);

  // WebSocket refs
  const stompClientRef = useRef(null);
  const isConnecting = useRef(false);
  const selectedCampaignKeyRef = useRef(selectedCampaignKey); // Add this ref

  // Update the ref whenever selectedCampaignKey changes
  useEffect(() => {
    selectedCampaignKeyRef.current = selectedCampaignKey;
  }, [selectedCampaignKey]);

  // 서버(우분투 머신) 시각 동기화: 최초 1회 + 이후 5분마다 시계 오차 보정
  useEffect(() => {
    syncServerTime();
    const timer = setInterval(syncServerTime, 5 * 60 * 1000);
    return () => clearInterval(timer);
  }, []);

  // Effect for discovering campaigns to populate the dropdown is now handled by WebSocket
  // The initial campaign list and updates will come from the WebSocket connection.
  // The isDiscoveryLoading state will be set to false once the first WebSocket message is processed.

  // Effect for fetching dashboard data when a campaign is selected
  useEffect(() => {
    if (selectedCampaignKey === null) return;

    const fetchDashboardData = async () => {
      setIsDashboardLoading(true);
      setDashboardError(null);
      setDashboardData(null);
      try {
        const data = await getCampaignDashboard(selectedCampaignKey);
        setDashboardData(data);
      } catch (err) {
        setDashboardError(err.message || '대시보드 정보를 가져오는 데 실패했습니다.');
      } finally {
        setIsDashboardLoading(false);
      }
    };

    fetchDashboardData();
  }, [selectedCampaignKey]);

  // Effect for WebSocket connection using STOMP
  useEffect(() => {
    if (isConnecting.current) return;

    isConnecting.current = true;

    // WebSocket URL 생성
    const getWebSocketUrl = () => {
      const wsBaseUrl = process.env.REACT_APP_WS_BASE_URL;

      // 개발 환경: HTTP URL 사용
      if (wsBaseUrl && wsBaseUrl.startsWith('http')) {
        const fullUrl = wsBaseUrl + '/ws-dashboard';
        return fullUrl;
      }

      // 프로덕션 환경: 상대 경로 사용
      if (wsBaseUrl && wsBaseUrl.startsWith('/')) {
        const protocol = window.location.protocol;
        const host = window.location.host;
        const fullUrl = `${protocol}//${host}${wsBaseUrl}`;
        return fullUrl;
      }

      // Fallback
      const protocol = window.location.protocol;
      const host = window.location.host;
      return `${protocol}//${host}/ws-dashboard`;
    };

    const WS_URL = getWebSocketUrl();

    const stompClient = new Client({
      webSocketFactory: () => new SockJS(WS_URL),
      debug: (str) => { },
      reconnectDelay: 5000,
      heartbeatIncoming: 4000,
      heartbeatOutgoing: 4000,

      onConnect: (frame) => {
        const subscription = stompClient.subscribe('/topic/dashboard-updates', (message) => {
          try {
            const updates = JSON.parse(message.body);

            const newCampaignKeys = Object.keys(updates).map(Number);
            const newCampaignNameMap = {};

            newCampaignKeys.forEach(key => {
              if (updates[key] && updates[key].campaignName) {
                newCampaignNameMap[key] = updates[key].campaignName;
              }
            });

            setCampaignKeys(newCampaignKeys);
            setCampaignNameMap(newCampaignNameMap);
            setIsDiscoveryLoading(false);

            let newSelectedKey = selectedCampaignKeyRef.current;
            if (!newSelectedKey || !newCampaignKeys.includes(newSelectedKey)) {
              newSelectedKey = newCampaignKeys.length > 0 ? newCampaignKeys[0] : null;
            }

            setSelectedCampaignKey(newSelectedKey);

            if (newSelectedKey && updates[newSelectedKey]) {
              setDashboardData(updates[newSelectedKey]);
            }
          } catch (error) {
            console.error('❌ Parse error:', error);
          }
        });
      },

      onStompError: (frame) => {
        console.error('========================================');
        console.error('❌ STOMP Error occurred!');
        console.error('Frame:', frame);
        console.error('Frame headers:', frame.headers);
        console.error('Frame body:', frame.body);
        console.error('========================================');
        setDashboardError('실시간 데이터 연결에 문제가 발생했습니다.');
        isConnecting.current = false;
        setIsDiscoveryLoading(false);
      },

      onWebSocketError: (error) => {
        console.error('========================================');
        console.error('❌ WebSocket Error occurred!');
        console.error('Error:', error);
        console.error('========================================');
      },

      onDisconnect: () => {
        console.warn('========================================');
        console.warn('⚠️ WebSocket disconnected');
        console.warn('========================================');
      }
    });

    stompClient.activate();
    stompClientRef.current = stompClient;

    return () => {
      if (stompClientRef.current?.connected) {
        stompClientRef.current.deactivate();
      }
      isConnecting.current = false;
    };
  }, []);

  const handleCampaignChange = (e) => {
    setSelectedCampaignKey(Number(e.target.value));
  };

  const renderDashboardContent = () => {
    if (isDashboardLoading) {
      return (
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status"></div>
          <p className="mt-3">대시보드 정보를 불러오는 중입니다...</p>
        </div>
      );
    }

    if (dashboardError) {
      return <div className="alert alert-danger">{dashboardError}</div>;
    }

    if (!dashboardData) {
      return <p>선택된 캠페인에 대한 데이터가 없습니다.</p>;
    }

    const hasTreasureHunt = dashboardData.treasureHuntEvents && dashboardData.treasureHuntEvents.length > 0;
    const hasRandomBox = dashboardData.randomBoxEvents && dashboardData.randomBoxEvents.length > 0;
    const hasRankedBox = dashboardData.rankedBoxEvents && dashboardData.rankedBoxEvents.length > 0;
    const hasLuckyBox = dashboardData.luckyBoxEvents && dashboardData.luckyBoxEvents.length > 0;
    const hasBasketball = dashboardData.basketBallEvents && dashboardData.basketBallEvents.length > 0;
    const hasArPoster = dashboardData.arPosterEvents && dashboardData.arPosterEvents.length > 0;

    return (
      <div className="mt-4">
        <h2>{dashboardData.campaignName}</h2>
        <div className="p-3 mb-3 bg-light rounded-3 border">
          <div className="d-flex align-items-center">
            <PeopleFill size={30} className="me-3" />
            <div>
              <h5 className="mb-0">총 가입자</h5>
              <span className="fs-4 fw-bold">{dashboardData.totalSignups}</span>
            </div>
          </div>
        </div>
        {hasTreasureHunt && <TreasureHuntReport treasureHuntEvents={dashboardData.treasureHuntEvents} />}
        {hasRandomBox && <GourdBreakingReport randomBoxEvents={dashboardData.randomBoxEvents} totalSignups={dashboardData.totalSignups} />}
        {(hasRankedBox || hasLuckyBox) && (
          <RankedGourdBreakingReport
            rankedBoxEvents={dashboardData.rankedBoxEvents}
            luckyBoxEvents={dashboardData.luckyBoxEvents}
            totalSignups={dashboardData.totalSignups}
          />
        )}
        {hasBasketball && <BasketballReport basketBallEvents={dashboardData.basketBallEvents} totalSignups={dashboardData.totalSignups} />}
        {hasArPoster && <ARPosterReport arPosterEvents={dashboardData.arPosterEvents} totalSignups={dashboardData.totalSignups} />}
        {!hasTreasureHunt && !hasRandomBox && !hasRankedBox && !hasLuckyBox && !hasBasketball && !hasArPoster && <p>이벤트 데이터가 없습니다.</p>}
      </div>
    );
  };

  if (isDiscoveryLoading) {
    return (
      <div className="d-flex flex-column justify-content-center align-items-center" style={{ height: '100vh' }}>
        <div className="spinner-border text-primary" role="status"></div>
        <div className="mt-3">캠페인 목록을 불러오는 중...</div>
      </div>
    );
  }

  return (
    <div className="container mt-4">
      <header className="text-center mb-4">
        <h1>WYFT 리포팅 페이지</h1>
      </header>

      <div className="p-4 mb-4 bg-light rounded-3 border">
        <label htmlFor="campaign-select" className="form-label h5">캠페인 선택</label>
        <select id="campaign-select" className="form-select" value={selectedCampaignKey} onChange={handleCampaignChange}>
          {campaignKeys.map(key => (
            <option key={key} value={key}>
              {campaignNameMap[key] || `캠페인 ${key}`}
            </option>
          ))}
        </select>
      </div>

      {renderDashboardContent()}
    </div>
  );
};

export default ReportPage;
